import { useState, useEffect, useCallback } from 'react';
import apiService from '../services/api';

// Debug: Check if apiService is properly imported
console.log('useApi.js - apiService loaded:', {
  available: !!apiService,
  type: typeof apiService,
  hasGetMyAvatars: apiService && typeof apiService.getMyAvatars === 'function',
  methodType: apiService && typeof apiService.getMyAvatars
});

// Generic hook for API calls
export const useApi = (apiCall, dependencies = []) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchData = useCallback(async (...args) => {
    try {
      setLoading(true);
      setError(null);
      const result = await apiCall(...args);
      setData(result);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, dependencies);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return { data, loading, error, refetch: fetchData };
};

// Hook for managing avatars
const AVATAR_PAGE_SIZE = 12;

export const useAvatars = (type = 'my', page = 1, search = '') => {
  const [avatars, setAvatars] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [totalPages, setTotalPages] = useState(0);
  const [total, setTotal] = useState(0);

  const fetchAvatars = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      console.log('fetchAvatars - Starting with type:', type);

      // Check if apiService is available
      if (!apiService || typeof apiService !== 'object') {
        throw new Error('API service is not properly initialized');
      }

      const apiCall = type === 'my' ? apiService.getMyAvatars : apiService.getPublicAvatars;

      if (!apiCall || typeof apiCall !== 'function') {
        throw new Error(`API method not found for type: ${type}`);
      }

      // 有搜索词时用较大 pageSize 拉取更多数据，本地过滤；无搜索词时正常分页
      const effectivePage = search ? 1 : page;
      const effectivePageSize = search ? 100 : AVATAR_PAGE_SIZE;
      console.log('fetchAvatars - Calling API with params:', { effectivePage, effectivePageSize, search });
      const result = await apiCall(effectivePage, effectivePageSize, search);
      console.log('fetchAvatars - API response:', result);

      // Handle backend response format
      if (result && result.code === 0) {
        const dataSource = Array.isArray(result?.avatars)
          ? result.avatars
          : Array.isArray(result?.data?.list)
            ? result.data.list
            : Array.isArray(result?.data?.records)
              ? result.data.records
              : Array.isArray(result?.data?.items)
                ? result.data.items
                : Array.isArray(result?.data?.avatars)
                  ? result.data.avatars
                  : Array.isArray(result?.data)
                    ? result.data
                    : [];

        const totalItems =
          result?.data?.total ??
          result?.data?.count ??
          result?.data?.totalCount ??
          result?.total ??
          result?.totalCount ??
          dataSource.length;

        const normalized = dataSource.map((avatar) => {
          const key =
            avatar.virtualman_key ||
            avatar.virtualmanKey ||
            avatar.key ||
            avatar.virtualmanId ||
            avatar.id;

          return {
            id: key || `avatar-${Date.now()}-${Math.random().toString(16).slice(2)}`,
            key,
            name: avatar.name || avatar.virtualmanName || '未命名数字人',
            thumbnail: avatar.preview_url || avatar.previewUrl || avatar.coverUrl || avatar.thumbnailUrl,
            createdAt: avatar.createdAt
              ? new Date(avatar.createdAt).toLocaleDateString('zh-CN')
              : avatar.createTime
                ? new Date(avatar.createTime).toLocaleDateString('zh-CN')
                : new Date().toLocaleDateString('zh-CN')
          };
        });

        setAvatars(normalized);
        setTotal(typeof totalItems === 'number' ? totalItems : normalized.length);

        const effectivePageSize = Number(result?.data?.pageSize || result?.pageSize || AVATAR_PAGE_SIZE) || AVATAR_PAGE_SIZE;
        const computedPages = totalItems > 0 ? Math.ceil(totalItems / effectivePageSize) : normalized.length > 0 ? 1 : 0;
        setTotalPages(computedPages);
      } else {
        console.warn('Unexpected API response format:', result);
        setAvatars([]);
        setTotalPages(0);
        setTotal(0);
      }
    } catch (err) {
      console.error('fetchAvatars - Error:', err.message);
      setError(err.message || 'Failed to fetch avatars');
      setAvatars([]);
      setTotalPages(0);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [type, page, search]);

  useEffect(() => {
    fetchAvatars();
  }, [fetchAvatars]);

  const createAvatar = async (formData) => {
    try {
      setLoading(true);
      await apiService.createAvatar(formData);
      await fetchAvatars(); // Refresh the list
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const deleteAvatar = async (avatarId) => {
    try {
      await apiService.deleteAvatar(avatarId);
      await fetchAvatars(); // Refresh the list
    } catch (err) {
      setError(err.message);
      throw err;
    }
  };

  return {
    avatars,
    loading,
    error,
    totalPages,
    total,
    refetch: fetchAvatars,
    createAvatar,
    deleteAvatar
  };
};

const VOICE_PAGE_SIZE = 12;

// Hook for managing voices
export const useVoices = (type = 'my', page = 1, search = '') => {
  const [voices, setVoices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [totalPages, setTotalPages] = useState(0);
  const [total, setTotal] = useState(0);

  const fetchVoices = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Check if apiService is available
      if (!apiService) {
        console.error('API service is not available, using mock voice data');
        // Use mock data as fallback
        const mockVoices = [
          {
            id: 'mock-voice-1',
            timbreKey: 'mock-voice-1',
            name: 'Mock Voice 1',
            description: 'Mock voice for testing',
            gender: 'female',
            language: '中文'
          },
          {
            id: 'mock-voice-2',
            timbreKey: 'mock-voice-2', 
            name: 'Mock Voice 2',
            description: 'Another mock voice',
            gender: 'male',
            language: '中文'
          }
        ];
        
        setVoices(mockVoices);
        setTotalPages(1);
        setTotal(mockVoices.length);
        return;
      }
      
      const apiCall = type === 'my' ? apiService.getMyVoices : apiService.getPublicVoices;

      if (!apiCall) {
        throw new Error(`API method not found for type: ${type}`);
      }

      // 有搜索词时用较大 pageSize 拉取更多数据，本地过滤；无搜索词时正常分页
      const effectivePage = search ? 1 : page;
      const effectivePageSize = search ? 100 : VOICE_PAGE_SIZE;
      console.log('Fetching voices:', { type, effectivePage, effectivePageSize, search });
      const result = await apiCall(effectivePage, effectivePageSize, search);
      console.log('Voice API response:', result);

      if (result && result.code === 0) {
        const dataSource = Array.isArray(result?.data?.list)
          ? result.data.list
          : Array.isArray(result?.data?.items)
            ? result.data.items
            : Array.isArray(result?.data?.records)
              ? result.data.records
              : Array.isArray(result?.data)
                ? result.data
                : [];

        const normalized = dataSource.map((voice) => {
          const timbreKey = voice.timbreKey || voice.timbre_key || voice.id;
          return {
            id: timbreKey || `voice-${Date.now()}-${Math.random().toString(16).slice(2)}`,
            key: timbreKey, // 添加 key 字段，与 StepTwo.jsx 保持一致
            timbreKey,
            name: voice.name || voice.displayName || '未命名声音',
            description: voice.description || voice.remark || '',
            gender: voice.gender || 'unknown',
            language: voice.language || '中文',
            sampleUrl: voice.sampleUrl || voice.sample_url || null,
            thumbnail: voice.thumbnail || voice.coverUrl || null,
            companyId: voice.companyId || voice.company_id || null, // 添加 companyId 字段
            createdAt: voice.createdAt
              ? new Date(voice.createdAt).toLocaleDateString('zh-CN')
              : voice.createTime
                ? new Date(voice.createTime).toLocaleDateString('zh-CN')
                : new Date().toLocaleDateString('zh-CN')
          };
        });

        const rawTotal =
          result?.data?.total ??
          result?.data?.totalCount ??
          result?.data?.count ??
          result?.total ??
          result?.totalCount ??
          normalized.length;

        const totalItems = Number(rawTotal);
        const safeTotal = Number.isFinite(totalItems) && totalItems >= 0 ? totalItems : normalized.length;

        const pageSize = Number(result?.data?.pageSize || result?.pageSize || VOICE_PAGE_SIZE) || VOICE_PAGE_SIZE;
        const computedPages = safeTotal ? Math.ceil(safeTotal / pageSize) : normalized.length ? 1 : 0;

        setVoices(normalized);
        setTotal(safeTotal);
        setTotalPages(computedPages);
      } else {
        console.warn('Unexpected voice API response format:', result);
        setVoices([]);
        setTotalPages(0);
        setTotal(0);
      }
    } catch (err) {
      console.error('Error fetching voices:', err);
      setError(err.message || 'Failed to fetch voices');
      setVoices([]);
      setTotalPages(0);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [type, page, search]);

  useEffect(() => {
    fetchVoices();
  }, [fetchVoices]);

  const cloneVoice = async (formData) => {
    try {
      setLoading(true);
      await apiService.cloneVoice(formData);
      await fetchVoices(); // Refresh the list
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const deleteVoice = async (voiceId) => {
    try {
      await apiService.deleteVoice(voiceId);
      await fetchVoices(); // Refresh the list
    } catch (err) {
      setError(err.message);
      throw err;
    }
  };

  return {
    voices,
    loading,
    error,
    totalPages,
    total,
    refetch: fetchVoices,
    cloneVoice,
    deleteVoice
  };
};

// Hook for managing videos
export const useVideos = (page = 1, search = '') => {
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [totalPages, setTotalPages] = useState(0);
  const [total, setTotal] = useState(0);

  const fetchVideos = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      console.log('Fetching videos:', { page, search });
      const result = await apiService.getMyVideos(page, 12, search);
      console.log('Video API response:', result);

      // Handle backend VideoListResponse format
      if (result && result.code === 0) {
        const videoList = result.videos || [];
        setVideos(videoList.map(video => ({
          id: video.task_id || `video-${Date.now()}`,
          taskId: video.task_id,
          title: `Generated Video ${video.task_id || 'Unknown'}`,
          status: video.status,
          statusText: getStatusText(video.status),
          url: video.url,
          audioUrl: video.audio_url,
          expireTime: video.expire_time,
          message: video.msg,
          createdAt: video.createdAt || new Date().toLocaleDateString('zh-CN'),
          thumbnail: null // Backend doesn't provide thumbnail
        })));
        setTotalPages(Math.ceil((result.total || 0) / 12));
        setTotal(result.total || 0);
      } else {
        console.warn('Unexpected video API response format:', result);
        setVideos([]);
        setTotalPages(0);
        setTotal(0);
      }
    } catch (err) {
      console.error('Error fetching videos:', err);
      setError(err.message || 'Failed to fetch videos');
      setVideos([]);
      setTotalPages(0);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [page, search]);

  // Helper function to convert status code to text
  const getStatusText = (status) => {
    switch (status) {
      case 1: return '排队中';
      case 2: return '正在生成';
      case 3: return '失败';
      case 4: return '成功';
      case 10: return '音频生成中';
      default: return '未知状态';
    }
  };

  useEffect(() => {
    fetchVideos();
  }, [fetchVideos]);

  const generateVideo = async (data) => {
    try {
      setLoading(true);
      const result = await apiService.generateVideo(data);
      await fetchVideos(); // Refresh the list
      return result;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const deleteVideo = async (videoId) => {
    try {
      await apiService.deleteVideo(videoId);
      await fetchVideos(); // Refresh the list
    } catch (err) {
      setError(err.message);
      throw err;
    }
  };

  return {
    videos,
    loading,
    error,
    totalPages,
    total,
    refetch: fetchVideos,
    generateVideo,
    deleteVideo
  };
};